import { useCallback, useState } from "react";

import { create } from "@bufbuild/protobuf";
import { styled } from "@linaria/react";
import { TrashIcon } from "@phosphor-icons/react";
import { match } from "ts-pattern";

import Button, { ButtonSize, ButtonVariant } from "@gx/dls/buttons/Button";
import FlexWrapper, { FlexDirection } from "@gx/dls/containers/FlexWrapper";
import Text, { TextSize, TextVariant, TextWeight } from "@gx/dls/text/Text";
import { PropsWithTheme, withTheme } from "@gx/dls/theme";
import Widget, { WidgetVariant } from "@gx/dls/widget/Widget";
import { DeleteSourceRequestSchema } from "@gx/ts-contracts/services/source/api/source_pb";

import DeleteConfirmDialog from "@/components/DeleteConfirmDialog";

import { useEditSourceFormContext } from "@/pages/sources/components/edit/hooks";

import { ToastVariant } from "@/providers/toast/Toast";
import { useToast } from "@/providers/toast/useToast";

import { useDeleteSourceMutation } from "@/api/mutations/sources";
import { isPermissionError } from "@/api/utils";

const DangerZoneWrapper = withTheme(styled.div<PropsWithTheme>`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 12px;
  border: 0.5px solid ${({ theme }) => theme.color.border.error};
  border-radius: 5px;
`);

interface EditSourceSettingsProps {
  onClose?: () => void;
}

const EditSourceSettings = ({ onClose }: EditSourceSettingsProps) => {
  const { state } = useEditSourceFormContext();
  const { mutate: deleteSource, isPending: isDeleteSourcePending } = useDeleteSourceMutation();
  const { showToast } = useToast();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleDeleteSource = useCallback(() => {
    deleteSource(create(DeleteSourceRequestSchema, { sourceId: state.source.id }), {
      onSuccess: () => {
        showToast({
          variant: ToastVariant.SUCCESS,
          header: "Source deleted",
          subheader: "The source has been deleted successfully",
        });
        handleCloseModal();
        if (onClose) onClose();
      },
      onError: (error) => {
        const isDenied = isPermissionError(error);
        showToast({
          variant: ToastVariant.ERROR,
          header: isDenied ? "Permission denied" : "Failed to delete source",
          subheader: isDenied
            ? "You don't have permission to delete sources"
            : "Something went wrong while deleting the source",
        });
      },
    });
  }, [deleteSource, state.source.id, showToast, onClose, handleCloseModal]);

  const renderConfiguration = useCallback(() => {
    return (
      match(state.source.sourceType)
        .returnType<React.ReactNode | null>()
        // .with(SourceType.WIKIPEDIA, () => <SourceSettingsWikipedia />)
        // .with(SourceType.BROWSERBASE, () => <SourceSettingsBrowserbase />)
        .otherwise(() => null)
    );
  }, [state.source.sourceType]);

  return (
    <>
      <DeleteConfirmDialog
        open={isModalOpen}
        title={`Are you sure you want to delete ${state.source.name}?`}
        body={
          <Text variant={TextVariant.SECONDARY}>
            All data associated with this source will be deleted. This action is immediate and
            cannot be undone.
          </Text>
        }
        confirmationPhrase={state.source.name}
        confirmLabel="Delete source"
        onClose={handleCloseModal}
        onConfirm={handleDeleteSource}
        isPending={isDeleteSourcePending}
      />
      <FlexWrapper direction={FlexDirection.COLUMN} gap={12} fillWidth>
        {renderConfiguration()}
        <Widget variant={WidgetVariant.PRIMARY} noHover fillWidth>
          <FlexWrapper direction={FlexDirection.COLUMN} gap={12} fillWidth>
            <Text size={TextSize.BODY_LG} variant={TextVariant.ERROR} weight={TextWeight.MEDIUM}>
              Danger zone
            </Text>
            <DangerZoneWrapper>
              <FlexWrapper direction={FlexDirection.COLUMN} gap={4}>
                <Text weight={TextWeight.MEDIUM}>Delete source</Text>
                <Text variant={TextVariant.SECONDARY}>
                  This will permanently delete this source and all of its data from the workspace.
                </Text>
              </FlexWrapper>
              <Button
                icon={TrashIcon}
                label="Delete source"
                variant={ButtonVariant.ERROR}
                size={ButtonSize.SMALL}
                onClick={handleOpenModal}
                isDisabled={isDeleteSourcePending}
              />
            </DangerZoneWrapper>
          </FlexWrapper>
        </Widget>
      </FlexWrapper>
    </>
  );
};

export default EditSourceSettings;
